/**
 * Commodity Valuation Service (KAN-300)
 * Frontend service for commodity MtM valuation API calls
 */

import { apiClient, APIResponse } from './client';

// ========== Types ==========

export interface RequiredPrice {
  fixingReference: string;
  label: string;
  tradeCount: number;
  price: number | null;
  currency: string;
  available: boolean;
}

export interface RequiredPricesResponse {
  valuationDate: string;
  requiredPrices: RequiredPrice[];
  allAvailable: boolean;
  missingCount: number;
}

export interface SavePriceEntry {
  fixingReference: string;
  price: number;
  currency: string;
}

export interface SavePricesResponse {
  valuationDate: string;
  savedCount: number;
  prices: SavePriceEntry[];
}

export interface StoredPrice {
  fixingReference: string;
  price: number;
  currency: string;
  source: string;
  enteredBy: string;
  enteredAt: string;
}

export interface StoredPricesResponse {
  valuationDate: string;
  prices: StoredPrice[];
}

export interface ValuationRunResult {
  uploadId: string;
  valuationDate: string;
  tradeCount: number;
  tradesSkipped: number;
  tradesErrored: number;
  totalUnrealisedPnl: number;
  pricesUsed: Record<string, number>;
  processingTimeSeconds: number;
  status: string;
  errors: Array<{ error?: string; tradeNumber?: string }>;
}

export interface ValuationUpload {
  id: string;
  type: string;
  uploadedBy: string;
  uploadDate: string;
  tradeCount: number;
  status: string;
  totalUnrealisedPnl: number;
  pricesUsed: Record<string, number>;
  errors: Array<{ error?: string; tradeNumber?: string }>;
  createdAt: string;
  processingTimeSeconds: number;
}

export interface ValuationTradeDetail {
  id: string;
  tradeId: string;
  tradeNumber: string;
  direction: string;
  lots: number;
  quantity: number;
  entryPrice: number;
  marketPrice: number;
  fixingReference: string;
  unrealisedPnl: number;
  currency: string;
  maturityDate: string;
  counterpartyName: string;
  ourParty: string;
  underlyingId: string;
  productType: string;
  underlyingName: string;
  lotSizeMultiplier: number;
  quantityConversionFactor: number;
  valuationDate: string;
  uploadId: string;
  uploadedAt: string;
}

export interface ValuationBatchResult {
  upload: ValuationUpload;
  trades: ValuationTradeDetail[];
}

// ========== Service ==========

class CommodityValuationService {
  /**
   * Get required prices — scan portfolio for fixing references that need prices.
   */
  async getRequiredPrices(
    clientId: string,
    valuationDate: string
  ): Promise<APIResponse<RequiredPricesResponse>> {
    return apiClient.get<RequiredPricesResponse>(
      `/api/v1/commodity-valuation/${clientId}/required-prices?valuationDate=${valuationDate}`
    );
  }

  /**
   * Save one or more commodity prices.
   */
  async savePrices(
    clientId: string,
    valuationDate: string,
    prices: SavePriceEntry[]
  ): Promise<APIResponse<SavePricesResponse>> {
    return apiClient.post<SavePricesResponse>(
      `/api/v1/commodity-valuation/${clientId}/prices`,
      { valuationDate, prices }
    );
  }

  /**
   * Get stored prices for a date.
   */
  async getStoredPrices(
    clientId: string,
    valuationDate: string
  ): Promise<APIResponse<StoredPricesResponse>> {
    return apiClient.get<StoredPricesResponse>(
      `/api/v1/commodity-valuation/${clientId}/prices?valuationDate=${valuationDate}`
    );
  }

  /**
   * Delete a stored commodity price for a given fixing reference and date.
   */
  async deletePrice(
    clientId: string,
    fixingReference: string,
    valuationDate: string
  ): Promise<APIResponse<{ fixingReference: string; valuationDate: string }>> {
    return apiClient.delete<{ fixingReference: string; valuationDate: string }>(
      `/api/v1/commodity-valuation/${clientId}/prices/${fixingReference}?valuationDate=${valuationDate}`
    );
  }

  /**
   * Trigger a commodity valuation run.
   */
  async calculate(
    clientId: string,
    valuationDate: string,
    forceRecalculate: boolean = false
  ): Promise<APIResponse<ValuationRunResult>> {
    return apiClient.post<ValuationRunResult>(
      `/api/v1/commodity-valuation/${clientId}/calculate`,
      { valuationDate, forceRecalculate }
    );
  }

  /**
   * List past valuation uploads.
   */
  async getUploads(
    clientId: string,
    limit: number = 20
  ): Promise<APIResponse<ValuationUpload[]>> {
    return apiClient.get<ValuationUpload[]>(
      `/api/v1/commodity-valuation/${clientId}/uploads?limit=${limit}`
    );
  }

  /**
   * Get full valuation detail for a historical batch.
   */
  async getBatch(
    clientId: string,
    uploadId: string
  ): Promise<APIResponse<ValuationBatchResult>> {
    return apiClient.get<ValuationBatchResult>(
      `/api/v1/commodity-valuation/${clientId}/batch/${uploadId}`
    );
  }

  /**
   * Delete a valuation batch (upload + history docs + trade valuation refs).
   */
  async deleteBatch(
    clientId: string,
    uploadId: string
  ): Promise<APIResponse<{ uploadId: string; tradesUpdated: number; historyDeleted: number }>> {
    return apiClient.delete<{ uploadId: string; tradesUpdated: number; historyDeleted: number }>(
      `/api/v1/commodity-valuation/${clientId}/batch/${uploadId}`
    );
  }
}

export const commodityValuationService = new CommodityValuationService();
